import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { MainHead } from '../../../components/common/head/MainHead';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useGetInformationsInfiniteQuery } from '../../../hooks/api/information/queries/useGetInformations.infinite.query';
import { useGetInformationsQuery } from '../../../hooks/api/information/queries/useGetInformations.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { useInfiniteScrollLoadingTarget } from '../../../hooks/scroll/useInfiniteScrollLoadingTarget';
import { useAppSelector } from '../../../hooks/store/useStore';
import { InfoHorizontalListSummaryViewGroup } from '../../../views/Info/listView/InfoHorizontalListSummaryView/InfoHorizontalListSummaryViewGroup';
import { InfoVerticalListView } from '../../../views/Info/listView/InfoVerticalListView/InfoVerticalListView';
import { InfoVerticalListViewGroup } from '../../../views/Info/listView/InfoVerticalListView/InfoVerticalListViewGroup';
import { InfoListPageCategorySection } from './components/InfoListPageCategorySection';
import { InfoListPageHeader } from './components/InfoListPageHeader';

export const InfoListPage = () => {
  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} headerTitle={'정보'} hasMyInfoButton />,
    fixedHeaderHeight: 40,
    fixedFooterHeight: 92,
  });

  const { data: informationsOfPopularTop5, isLoading: informationPopularTop5QueryIsLoading } = useGetInformationsQuery({
    searchParam: { type: 'popular', count: 5 },
  });

  const { selectedCategory } = useAppSelector((state) => state.infoCategory);

  const searchParam = useMemo<{ category?: 'MAGAZINE' | 'NEWS'; count: number }>(() => {
    const count = 5;

    if (selectedCategory === 'MAGAZINE') {
      return { category: 'MAGAZINE', count };
    }

    if (selectedCategory === 'NEWS') {
      return { category: 'NEWS', count };
    }

    return { count };
  }, [selectedCategory]);

  const {
    data: informations,
    isLoading: isGetInformationsQueryLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetInformationsInfiniteQuery({
    searchParam,
  });

  const { loadingTargetRef, isLoadingTargetRendered } = useInfiniteScrollLoadingTarget({
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    triggeredFnWhenInView: fetchNextPage,
  });

  if (informationPopularTop5QueryIsLoading) {
    return <AnimateLoading />;
  }

  return (
    <>
      <Helmet>
        <title>{pageInfo.infoListPage.title}</title>
      </Helmet>

      {informationsOfPopularTop5 && (
        <InfoListPageHeader>
          <InfoHorizontalListSummaryViewGroup informations={informationsOfPopularTop5} />
        </InfoListPageHeader>
      )}

      <InfoListPageCategorySection />

      {isGetInformationsQueryLoading && <AnimateLoading />}

      {informations && (
        <>
          <InfoVerticalListViewGroup>
            {informations.pages.map((page) =>
              page.map((information) => <InfoVerticalListView key={information.id} information={information} />),
            )}
          </InfoVerticalListViewGroup>

          {isLoadingTargetRendered && <LoadingTarget ref={loadingTargetRef} />}
        </>
      )}
    </>
  );
};

const LoadingTarget = styled.div``;
